<script lang="ts">
  import { onMount } from 'svelte';
  import { appTheme, authProvider, pollingActive, pollError, pollErrorDetail, startSync } from '../lib/stores';

  let { onLogout, onSwitch, onSuperAdmin, canSwitch = false, canSupportView = false,
    switching = false, switchError = '', justConnected = false }: {
    onLogout: () => void;
    onSwitch: () => void;
    onSuperAdmin: () => void;
    canSwitch?: boolean;
    canSupportView?: boolean;
    switching?: boolean;
    switchError?: string;
    // App strips ?fund_connected before this mounts, so it passes the flag in.
    justConnected?: boolean;
  } = $props();

  let mailboxConnected = $state<boolean | null>(null);
  const connectionError = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('auth_error') : null;
  onMount(async () => {
    // A refresh must not replay an old denial; the button below retries it.
    if (connectionError) history.replaceState({}, '', window.location.pathname);
    try {
      const response = await fetch('/api/auth/fund/connection', { credentials: 'include' });
      mailboxConnected = response.ok ? !!(await response.json()).connected : null;
      if (mailboxConnected === false && !connectionError && !justConnected) connectMailbox();
    } catch { mailboxConnected = null; }
  });

  function connectMailbox() {
    window.location.assign($authProvider === 'microsoft'
      ? '/api/auth/fund/microsoft/connect' : '/api/auth/fund/google/connect');
  }
</script>

<div class="wall">
  <div class="card">
    <img src={$appTheme === 'dark' ? '/mark-dark.svg' : '/mark.svg'} alt="Dealton" class="mark" />
    <div class="eyebrow">Dealton Finance · Early access</div>
    <h1>Coming in 2 weeks</h1>
    <p>Your Investor Relations workspace is on its way. We’ll let you know when it’s ready.</p>
    {#if connectionError}<p class="error" role="alert">Read-only mailbox access was not approved. Mailbox sync is paused until you approve it.</p>{/if}
    {#if mailboxConnected === false}
      <button type="button" onclick={connectMailbox}>Approve read-only access</button>
    {:else if mailboxConnected}
      {#if $pollError}
        <p class="error" role="alert">Sync needs attention: {$pollErrorDetail || 'Please try again.'}</p>
        <button type="button" onclick={() => void startSync()} disabled={$pollingActive}>Retry read-only sync</button>
      {/if}
    {/if}
    <div class="links">
      <a href="https://dealton.ai/privacy.html" target="_blank" rel="noopener">Privacy</a>
      <a href="mailto:support@dealton.ai?subject=Fund%20early%20access">Contact us or revoke access</a>
    </div>
    {#if canSwitch || canSupportView}
      <div class="internal">
        {#if canSwitch}<button type="button" onclick={onSwitch} disabled={switching}>{switching ? 'Switching…' : 'Switch organization to Corporate / Sales'}</button>{/if}
        {#if canSupportView}<button type="button" onclick={onSuperAdmin}>Super Admin</button>{/if}
        {#if switchError}<div role="alert" class="error">{switchError}</div>{/if}
      </div>
    {/if}
    <button class="logout" type="button" onclick={onLogout}>Log out</button>
  </div>
</div>

<style>
  .wall { height: 100vh; height: 100dvh; display: flex; align-items: center; justify-content: center; overflow: hidden; padding: 24px; background: var(--bg); color: var(--text); }
  .card { width: min(100%, 560px); max-height: 100%; overflow-y: auto; padding: 40px; border: 1px solid var(--border); border-radius: 18px; background: var(--bg-surface); box-shadow: 0 18px 60px #0001; }
  .mark { width: 38px; height: 38px; margin-bottom: 28px; }
  .eyebrow { font-size: 12px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--accent); }
  h1 { margin: 12px 0 18px; font-size: clamp(32px, 6vw, 44px); letter-spacing: -.04em; }
  p { margin: 0 0 16px; line-height: 1.6; color: var(--text-muted); }
  .links { display: flex; flex-wrap: wrap; gap: 18px; margin: 24px 0; font-size: 13px; }
  a { color: var(--accent); }
  .internal { display: grid; gap: 10px; margin: 26px 0; padding-top: 18px; border-top: 1px solid var(--border); }
  button { padding: 10px 14px; border: 1px solid var(--border); border-radius: 8px; background: var(--bg); color: var(--text); cursor: pointer; text-align: left; }
  button:disabled { opacity: .6; cursor: wait; }
  .logout { margin-top: 6px; background: transparent; }
  .error { color: var(--red-ink); font-size: 13px; }
  @media (max-width: 600px), (max-height: 650px) {
    .wall { padding: 16px; }
    .card { padding: 24px; }
  }
</style>
